import React, { FC, useEffect, useMemo, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KButton, KDropdown, KInput, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { systems } from "../../../../../apis"
import { IntercomTriageAssignee, IntercomTriageKeyword } from "../../../../../types"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import { toast } from "react-toastify"

type IntercomKeywordsProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

type EditableKeyword = IntercomTriageKeyword & {
  localId: string
}

const CATEGORY_OPTIONS = [
  { value: "payment", label: "Payment" },
  { value: "bug", label: "Technical Bug" },
  { value: "operations", label: "Operations" }
]

const formatSlackMentionPreview = (slackMemberId: string) => {
  const normalizedSlackMemberId = slackMemberId.trim()
  if (!normalizedSlackMemberId) return ""
  return `<@${normalizedSlackMemberId}>`
}

const IntercomKeywords: FC<IntercomKeywordsProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [keywordList, setKeywordList] = useState<EditableKeyword[]>([])
  const [initialKeywordMap, setInitialKeywordMap] = useState<Record<number, IntercomTriageKeyword>>({})
  const [assigneeMap, setAssigneeMap] = useState<Record<string, IntercomTriageAssignee>>({})
  const [initialAssigneeMap, setInitialAssigneeMap] = useState<Record<string, IntercomTriageAssignee>>({})
  const [slackMemberOptions, setSlackMemberOptions] = useState<KSelectOption[]>([])
  const [deletedIdList, setDeletedIdList] = useState<number[]>([])
  const [hasChanges, setHasChanges] = useState(false)

  const fetchKeywordList = async () => {
    setLoading(true)
    try {
      const [keywordResp, assigneeResp, slackMembersResp] = await Promise.all([
        systems(`/intercom-triage-keyword/`, "GET"),
        systems(`/intercom-triage-assignee/`, "GET"),
        systems(`/intercom-triage-assignee/slack_members/`, "GET")
      ])

      const nextKeywordList = (keywordResp.data.results || []).map((item: IntercomTriageKeyword) => ({
        ...item,
        localId: `existing-${item.id}`
      }))

      const nextInitialMap = nextKeywordList.reduce<Record<number, IntercomTriageKeyword>>((acc, item) => {
        if (item.id) {
          acc[item.id] = {
            id: item.id,
            category: item.category,
            keyword: item.keyword,
            created: item.created,
            updated: item.updated
          }
        }
        return acc
      }, {})

      const nextAssigneeMap = (assigneeResp.data.results || []).reduce<Record<string, IntercomTriageAssignee>>(
        (acc, item: any) => {
          acc[item.category] = {
            id: item.id,
            category: item.category,
            slackMemberId: item.slack_member_id || "",
            created: item.created,
            updated: item.updated
          }
          return acc
        },
        {}
      )

      const nextSlackMemberOptions = [
        { value: "", value2: "", label: "No assignee" },
        ...(slackMembersResp.data.results || []).map((item: any) => ({
          value: item.id,
          value2: item.id,
          label: item.real_name ? `${item.display_name} (${item.real_name})` : item.display_name
        }))
      ]

      setKeywordList(nextKeywordList)
      setInitialKeywordMap(nextInitialMap)
      setAssigneeMap(nextAssigneeMap)
      setInitialAssigneeMap(nextAssigneeMap)
      setSlackMemberOptions(nextSlackMemberOptions)
      setDeletedIdList([])
      setHasChanges(false)
    } catch {
      toast.error("Error fetching Intercom keywords", errorFilledToastOptions)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchKeywordList()
  }, [])

  const groupedKeywordList = useMemo(() => {
    return CATEGORY_OPTIONS.map((option) => ({
      ...option,
      items: keywordList.filter((item) => item.category === option.value)
    }))
  }, [keywordList])

  const updateKeyword = (localId: string, field: "category" | "keyword", value: string) => {
    setKeywordList((prevList) =>
      prevList.map((item) => (item.localId === localId ? { ...item, [field]: value } : item))
    )
    setHasChanges(true)
  }

  const addKeywordRow = (category: string) => {
    setKeywordList((prevList) => [
      ...prevList,
      {
        localId: `new-${Date.now()}-${Math.random()}`,
        category,
        keyword: ""
      }
    ])
    setHasChanges(true)
  }

  const updateSlackMemberId = (category: string, slackMemberId: string) => {
    setAssigneeMap((prevMap) => ({
      ...prevMap,
      [category]: {
        ...(prevMap[category] || { category }),
        category,
        slackMemberId
      }
    }))
    setHasChanges(true)
  }

  const getSelectedSlackMemberOption = (category: string) => {
    const slackMemberId = assigneeMap[category]?.slackMemberId || ""
    return slackMemberOptions.find((option) => option.value === slackMemberId)
  }

  const removeKeywordRow = (localId: string) => {
    setKeywordList((prevList) => {
      const currentItem = prevList.find((item) => item.localId === localId)
      if (currentItem?.id) {
        setDeletedIdList((prevDeletedIdList) => [...new Set([...prevDeletedIdList, currentItem.id!])])
      }
      return prevList.filter((item) => item.localId !== localId)
    })
    setHasChanges(true)
  }

  const onSave = async () => {
    const normalizedList = keywordList.map((item) => ({
      ...item,
      keyword: item.keyword.trim()
    }))

    if (normalizedList.some((item) => item.keyword.length === 0)) {
      toast.error("Keyword cannot be empty", errorFilledToastOptions)
      return
    }

    setSaving(true)
    try {
      const createList = normalizedList.filter((item) => !item.id)
      const updateList = normalizedList.filter(
        (item) =>
          !!item.id &&
          (initialKeywordMap[item.id!]?.keyword !== item.keyword || initialKeywordMap[item.id!]?.category !== item.category)
      )
      const assigneeRequests = CATEGORY_OPTIONS.map((option) => {
        const currentAssignee = assigneeMap[option.value] || {
          category: option.value,
          slackMemberId: ""
        }
        const initialAssignee = initialAssigneeMap[option.value]
        const normalizedSlackMemberId = currentAssignee.slackMemberId.trim()

        if (!initialAssignee?.id) {
          return systems(`/intercom-triage-assignee/`, "POST", {
            category: option.value,
            slack_member_id: normalizedSlackMemberId
          })
        }

        if ((initialAssignee.slackMemberId || "") === normalizedSlackMemberId) {
          return null
        }

        return systems(`/intercom-triage-assignee/${initialAssignee.id}/`, "PATCH", {
          category: option.value,
          slack_member_id: normalizedSlackMemberId
        })
      }).filter(Boolean)

      await Promise.all([
        ...createList.map((item) =>
          systems(`/intercom-triage-keyword/`, "POST", {
            category: item.category,
            keyword: item.keyword
          })
        ),
        ...updateList.map((item) =>
          systems(`/intercom-triage-keyword/${item.id}/`, "PATCH", {
            category: item.category,
            keyword: item.keyword
          })
        ),
        ...deletedIdList.map((id) => systems(`/intercom-triage-keyword/${id}/`, "DELETE")),
        ...assigneeRequests
      ])

      toast.success("Successfully saved Intercom keywords", successFilledToastOptions)
      await fetchKeywordList()
    } catch (e) {
      console.error(e)
      toast.error("Error saving Intercom keywords", errorFilledToastOptions)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8" style={{ paddingTop: 22 }}>
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text="Intercom Keywords" />
          </div>
        </KTooltip>
      </div>
      <div className="w-[100%] p-4">
        {(loading || saving) && <Loading />}
        <div className="mb-4">
          <div className={"border-b-2 flex"} style={{ width: "100%" }}>
            <div style={{ width: "18%" }}>
              <KSpan text="Category" color="#111" fontWeight={600} />
            </div>
            <div style={{ width: "47%" }}>
              <KSpan text="Keyword" color="#111" fontWeight={600} />
            </div>
            <div style={{ width: "15%" }}>
              <KSpan text="Updated" color="#111" fontWeight={600} />
            </div>
            <div style={{ width: "20%" }}>
              <KSpan text="Actions" color="#111" fontWeight={600} />
            </div>
          </div>
          {groupedKeywordList.map((group) => (
            <div className="mb-6 mt-4" key={group.value}>
              <div
                style={{ borderBottom: "1px solid #111111", paddingBottom: 10 }}
                className="flex justify-between items-center gap-4"
              >
                <div className="flex items-center gap-4">
                  <KTitleSpan fontSize={18} lineHeight="auto" text={group.label} />
                  <div className="flex flex-col gap-1">
                    <div style={{ width: 220 }}>
                      <KDropdown
                        width={220}
                        options={slackMemberOptions}
                        selected={getSelectedSlackMemberOption(group.value)}
                        onSelect={(selected) => updateSlackMemberId(group.value, String((selected as KSelectOption)?.value || ""))}
                        placeholder="Select Slack member"
                      />
                    </div>
                    {assigneeMap[group.value]?.slackMemberId?.trim() && (
                      <KSpan
                        text={`Slack Member ID: ${assigneeMap[group.value]?.slackMemberId} · Mention: ${formatSlackMentionPreview(assigneeMap[group.value]?.slackMemberId || "")}`}
                        fontSize={12}
                        lineHeight="16px"
                        color="#6B7280"
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div style={{ minWidth: 148 }}>
                    <KButton
                      width="148px"
                      height="40px"
                      padding="8px 16px"
                      text="Add Keyword"
                      onClick={() => addKeywordRow(group.value)}
                    />
                  </div>
                </div>
              </div>
              {group.items.map((item) => (
                <div className={"border-b-2 flex p-2 items-center"} style={{ width: "100%" }} key={item.localId}>
                  <div style={{ width: "18%" }}>
                    <select
                      value={item.category}
                      onChange={(event) => updateKeyword(item.localId, "category", event.target.value)}
                      className="border rounded-[8px] p-2 w-[90%]"
                    >
                      {CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: "47%" }}>
                    <KInput
                      value={item.keyword}
                      onChange={(text) => updateKeyword(item.localId, "keyword", text)}
                    />
                  </div>
                  <div style={{ width: "15%" }}>
                    <KSpan text={item.updated?.substring(0, 10) || "-"} />
                  </div>
                  <div style={{ width: "20%" }}>
                    <KButton
                      width="90px"
                      height="32px"
                      text="Remove"
                      background="#F4F4F5"
                      color="#111"
                      onClick={() => removeKeywordRow(item.localId)}
                    />
                  </div>
                </div>
              ))}
              {group.items.length === 0 && (
                <div className="border-b-2 flex p-3">
                  <KSpan text="No keywords yet" color="#6B7280" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <KButton
            width="120px"
            dataTestId="intercom-keywords-save-button"
            disabled={!hasChanges || saving}
            onClick={() => onSave()}
            text="Save"
          />
        </div>
      </div>
    </div>
  )
}

export default IntercomKeywords
